import { computed, contentChild, Directive, ElementRef, inject, input, Renderer2 } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router } from '@angular/router'
import { combineLatestWith, map, startWith, takeUntil } from 'rxjs'

import { filterIfInstanceOf } from 'app/utils/rxjs/filter-if-instance-of.operator'
import { onDestroy } from 'app/utils/rxjs/on-destroy.observable'

import { CmsLinkDirective } from './cms-link.directive'

@Directive({ selector: '[paiCmsLinkActive]' })
export class CmsLinkActiveDirective {
  readonly className = input.required<string>({ alias: 'paiCmsLinkActive' })

  protected readonly link = computed((): CmsLinkDirective => {
    const link = this.sameLevelLink || this.nestedLink()
    if (!link) {
      throw new Error('Neither the host not a child uses the CmsLinkDirective')
    }
    return link
  })
  protected readonly linkUrl = computed(() => {
    return this.link().href()?.replace(/(#.*)/, '') ?? ''
  })
  private readonly nestedLink = contentChild(CmsLinkDirective)
  private readonly sameLevelLink = inject(CmsLinkDirective, { optional: true })

  private readonly renderer = inject(Renderer2)
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef)

  constructor() {
    const router = inject(Router)

    router.events
      .pipe(
        takeUntil(onDestroy()),
        filterIfInstanceOf(NavigationEnd),
        map((event) => event.urlAfterRedirects),
        startWith(router.url),
        map((url) => url.replaceAll(/;[^/]*/g, '')),
        combineLatestWith(toObservable(this.className)),
      )
      .subscribe(this.update)

    // if the className would change, we need to remove the old one.
    // we also remove on destroy.
    // but since we never have this case, we dont use this code ftm
    /*
    effect((onCleanup) => {
      const className = this.className()
      onCleanup(() => this.renderer.removeClass(this.element.nativeElement, className))
    })
    */
  }

  private readonly update = ([newUrl, className]: [string, string]) => {
    if (newUrl.startsWith(this.linkUrl())) {
      this.renderer.addClass(this.element.nativeElement, className)
    } else {
      this.renderer.removeClass(this.element.nativeElement, className)
    }
  }
}
